import { memo, useState, useCallback } from 'react';
import type { PasswordGameRound, ScreenPosition, CharacterPortrait } from '../types';
import { DialogueBox } from './dialogue-box';
import styles from './password-game.module.css';

const DEFAULT_SCREEN_POS: ScreenPosition = { top: 5, left: 17, width: 66, height: 58 };

type RoundPhase = 'playing' | 'feedback';
type GamePhase = RoundPhase | 'victory';

interface PasswordGameProps {
  rounds: PasswordGameRound[];
  victoryReward: string;
  screenPosition?: ScreenPosition;
  characterData?: CharacterPortrait[];
  /** When true, hide the "Menu" button on victory so the player continues the story (Suivant) or restarts */
  hasNextScene?: boolean;
  onComplete: (result: {
    stars: number;
    totalRounds: number;
    action: 'restart' | 'menu' | 'next';
  }) => void;
}

const SHIELD_SVG = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#5b9bd5"/><stop offset="100%" stop-color="#3a7abd"/></linearGradient></defs><path d="M32 4 L56 16 V34 C56 48 32 60 32 60 C32 60 8 48 8 34 V16 Z" fill="url(#g)" stroke="#2a5a8a" stroke-width="2"/><path d="M26 32 L30 36 L38 26" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`)}`;

const SCORE_LABELS = ['Très faible', 'Bien mais pas top', 'Fort'];

const SCORE_DESCRIPTIONS = [
  'Ce mot de passe peut être retrouvé à partir de vos informations personnelles',
  'Ce mot de passe peut être retrouvé rapidement par un hacker',
  'Ce mot de passe prendra énormément de temps à être retrouvé par un hacker et ne peut être déduit de vos informations personnelles',
];

export const PasswordGame = memo(({ rounds, victoryReward, screenPosition, characterData = [], hasNextScene, onComplete }: PasswordGameProps) => {
  const pos = screenPosition || DEFAULT_SCREEN_POS;
  const screenStyle = {
    top: `${pos.top}%`,
    left: `${pos.left}%`,
    width: `${pos.width}%`,
    height: `${pos.height}%`,
  };

  const [currentRound, setCurrentRound] = useState(0);
  const [phase, setPhase] = useState<GamePhase>('playing');
  const [selectedLevel, setSelectedLevel] = useState(0);
  const [stars, setStars] = useState(0);
  const [roundResults, setRoundResults] = useState<boolean[]>([]);

  const round = rounds[currentRound]!;

  const handleSubmit = useCallback(() => {
    if (phase !== 'playing' || selectedLevel === 0) return;
    const isCorrect = selectedLevel === round.expectedScore;
    const newStars = stars + (isCorrect ? 1 : 0);
    setStars(newStars);
    setRoundResults(prev => [...prev, isCorrect]);
    setPhase('feedback');
  }, [phase, selectedLevel, round.expectedScore, stars]);

  const handleNext = useCallback(() => {
    if (currentRound < rounds.length - 1) {
      setCurrentRound(prev => prev + 1);
      setPhase('playing');
      setSelectedLevel(0);
    } else {
      setPhase('victory');
    }
  }, [currentRound, rounds.length]);

  const handleVictoryAction = useCallback((action: 'restart' | 'menu' | 'next') => {
    if (action === 'restart') {
      setCurrentRound(0);
      setStars(0);
      setRoundResults([]);
      setPhase('playing');
      setSelectedLevel(0);
      return;
    }
    onComplete({ stars, totalRounds: rounds.length, action });
  }, [onComplete, stars, rounds.length]);

  const renderStars = (count: number, total: number) => {
    return Array.from({ length: total }, (_, i) => (
      <span key={i} style={{ display: 'inline-block', transition: 'transform 0.3s', transform: i < count ? 'scale(1.1)' : 'scale(1)' }}>
        {i < count ? '\u2B50' : '\u2606'}
      </span>
    ));
  };

  if (phase === 'victory') {
    return (
      <div className={styles.overlay}>
        <div className={styles.screenArea} style={screenStyle}>
          <div className={styles.screenContent} style={{ position: 'relative' }}>
            <div className={styles.victoryOverlay}>
              <div className={styles.victoryCard}>
                <div className={styles.victoryTitle}>Mission Terminée !</div>
                <div className={styles.victoryStars}>
                  {renderStars(stars, rounds.length)}
                </div>
                <div className={styles.victoryStarsText}>
                  {stars} / {rounds.length} bonnes réponses
                </div>
                <div className={styles.victoryReward}>
                  <div className={styles.rewardLabel}>Récompense</div>
                  <div className={styles.rewardName}>{victoryReward}</div>
                </div>
                <div className={styles.victoryButtons}>
                  <button
                    className={`${styles.victoryButton} ${styles.restartBtn}`}
                    onClick={() => handleVictoryAction('restart')}
                  >
                    Recommencer
                  </button>
                  {!hasNextScene && (
                    <button
                      className={`${styles.victoryButton} ${styles.menuBtn}`}
                      onClick={() => handleVictoryAction('menu')}
                    >
                      Menu
                    </button>
                  )}
                  <button
                    className={`${styles.victoryButton} ${styles.nextBtn}`}
                    onClick={() => handleVictoryAction('next')}
                  >
                    {hasNextScene ? 'Continuer' : 'Suivant'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.overlay}>
      {/* Round counter */}
      <div className={styles.roundCounter}>
        Profil {currentRound + 1} / {rounds.length}
      </div>

      {/* Frank's dialogue using the standard DialogueBox */}
      <DialogueBox
        speaker="Frank"
        text={round.frankQuestion}
        onClick={() => {}}
        showContinue={false}
        characterId="frank"
        characterData={characterData}
      />

      {/* 3-panel UI inside laptop screen */}
      <div className={styles.screenArea} style={screenStyle}>
        <div className={styles.screenContent} style={{ position: 'relative' }}>
          {/* Left: Profile */}
          <div className={styles.profilePanel}>
            <div className={styles.profileTitle}>Profil Utilisateur</div>
            <ProfileField label="Nom" value={round.profile.name} />
            <ProfileField label="Age" value={round.profile.age} />
            <ProfileField label="Poste" value={round.profile.position} />
            <ProfileField label="Ville" value={round.profile.city} />
            {round.profile.partner && (
              <ProfileField label="Conjoint(e)" value={round.profile.partner} />
            )}
            {round.profile.pet && (
              <ProfileField label="Animal" value={round.profile.pet} />
            )}
            <ProfileField label="Naissance" value={round.profile.birthDate} />
            <ProfileField label="Reseaux" value={round.profile.socialMedia} />
          </div>

          {/* Center: Password */}
          <div className={styles.passwordPanel}>
            <div className={styles.passwordTitle}>Mot de Passe</div>
            <div className={styles.passwordDisplay}>
              {round.password}
            </div>
          </div>

          {/* Right: Evaluation */}
          <div className={styles.evalPanel}>
            <div className={styles.evalTitle}>Analyse de la Force{'\n'}du Mot de Passe</div>
            <img src={SHIELD_SVG} alt="Shield" className={styles.shieldIcon} />
            <div className={styles.levelButtons}>
              {SCORE_LABELS.map((label, i) => {
                const level = i + 1;
                const isSelected = selectedLevel === level;
                const levelClass = level === 1 ? styles.levelWeak : level === 2 ? styles.levelMedium : styles.levelStrong;
                return (
                  <button
                    key={level}
                    className={`${styles.levelButton} ${levelClass} ${isSelected ? styles.levelButtonSelected : ''}`}
                    onClick={() => phase === 'playing' && setSelectedLevel(level)}
                    disabled={phase !== 'playing'}
                  >
                    <span className={styles.levelLabel}>{label}</span>
                    <span className={styles.levelDescription}>{SCORE_DESCRIPTIONS[i]}</span>
                  </button>
                );
              })}
            </div>
            <button
              className={styles.submitButton}
              onClick={handleSubmit}
              disabled={phase !== 'playing' || selectedLevel === 0}
            >
              Soumettre l&apos;Evaluation
            </button>
          </div>

          {/* Feedback overlay */}
          {phase === 'feedback' && (
            <div className={styles.feedbackOverlay}>
              <div className={styles.feedbackCard}>
                <div className={`${styles.feedbackResult} ${roundResults[currentRound] ? styles.feedbackCorrect : styles.feedbackWrong}`}>
                  {roundResults[currentRound]
                    ? 'Bonne réponse !'
                    : `Réponse attendue : ${SCORE_LABELS[round.expectedScore - 1]}`}
                </div>
                <div className={styles.feedbackStars}>
                  {roundResults[currentRound] ? '\u2B50' : '\u274C'}
                </div>
                <div className={styles.feedbackText}>{round.feedback}</div>
                <button className={styles.nextButton} onClick={handleNext}>
                  Suivant
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

PasswordGame.displayName = 'PasswordGame';

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.profileField}>
      <div className={styles.profileLabel}>{label}</div>
      <div className={styles.profileValue}>{value}</div>
    </div>
  );
}
